<template>
  <div class="conv-panel" :class="{ '-show-filters': filtersOpen }">
    <h1 class="page-title">Conversations</h1>

    <conversationFlag :conversation="currentConversation" />
    <testModeToggle :on-change="onTestModeChange" />

    <div class="conv-page-content">
      <section class="conv-side">
        <a href="#" class="button -not-rounded -full-width -small -toggle-filters" @click.prevent="toggleFilters()"
          >{{ filtersOpen ? "Hide" : "Show" }} filters</a
        >

        <form class="conv-filters">
          <!--           Status:
          <select v-model="filters.status">
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select> -->

          Flag status:
          <select v-model="filters.flag_status">
            <option value="all">All</option>
            <option value="flagged">Flagged</option>
            <option value="resolved">Resolved</option>
          </select>

          <div class="conv-input-field">
            From:
            <VueDatePicker
              v-model="filters.from"
              :enable-time-picker="false"
              auto-apply
              format="yyyy-MM-dd"
              :max-date="filters.to"
              @update:model-value="onFilterFromChange"
            />
          </div>
          <div class="conv-input-field">
            To:
            <VueDatePicker
              v-model="filters.to"
              :enable-time-picker="false"
              auto-apply
              format="yyyy-MM-dd"
              :min-date="filters.from"
              @update:model-value="onFilterToChange"
            />
          </div>

          <label>Customer name: <input v-model="filters.name" type="text" /></label>

          <label>
            Dealership:
            <select v-model="filters.dealership_id">
              <option value="">-</option>
              <option v-for="dealership in dealerships" :key="dealership.id" :value="dealership.id">
                {{ dealership.name }}
              </option>
            </select>
          </label>

          <div v-for="filter in filters.context" :key="filter.label">
            <label class="conv-input-field">
              {{ filter.label }}:
              <input v-model="filter.value" type="text" />
            </label>
          </div>

          <label class="flex-label">
            <input v-model="filters.inc_blacklisted" type="checkbox" />
            Include blacklisted
          </label>

          <action-button class="button -mt" :text="'Filter'" :state="filterState" @pressed="doFilter"></action-button>
        </form>

        <aside class="conv-list">
          <div v-if="fetchingStatus === 'loading' && conversations.length === 0" class="conv-label empty">
            Fetching conversations...
          </div>

          <div v-if="fetchingStatus === 'failed' && conversations.length === 0" class="conv-label empty">
            Failed to fetch conversations
            <br />
            <a href="#" @click.prevent="initialFetch">Retry</a>
          </div>

          <div v-if="fetchingStatus === 'ready' && conversations.length === 0" class="conv-label empty">
            No conversations found
          </div>

          <conversation-label
            v-for="conversation in activeConversations"
            :key="conversation.id"
            :active="currentConversation && conversation.id === currentConversation.id"
            :conversation="conversation"
            @select="changeConversation"
          />

          <action-button
            v-if="hasMoreConversations"
            class="button -full-width -not-rounded"
            :text="'Load More'"
            :state="loadMoreState"
            @pressed="loadMore"
          ></action-button>
        </aside>
      </section>

      <section v-if="currentConversation" class="conv-current">
        <span v-if="loadingMessages" class="conv-loading">Loading...</span>
        <message-list v-else :messages="messagesForConversation(currentConversation.id)" />

        <div v-if="currentConversation.human_in_the_loop" class="conv-take-control">
          <reply-box @submit="postMessage" />
          <span class="conv-auto-reply" @click="takeControl(false)">Enable auto-reply</span>
        </div>

        <div v-else class="conv-take-control">
          <span class="button" @click="takeControl(true)">Take control of conversation</span>
          <p>Note: This will disable the bots auto-reply.</p>
        </div>
      </section>

      <section v-if="currentConversation" class="conv-details">
        <ul>
          <li v-if="currentConversation.blacklisted" class="blacklisted"><strong>BLACKLISTED</strong></li>

          <li class="title mt0">Conversation details</li>
          <li><strong>ID</strong> {{ currentConversation.id }}</li>
          <li><strong>Title</strong> {{ currentConversation.metadata.context.title }}</li>
          <li>
            <strong>URL</strong>
            <a :href="currentConversation.metadata.context.url" target="_blank">{{
              currentConversation.metadata.context.url
            }}</a>
          </li>
        </ul>

        <ul>
          <li class="title">Customer details</li>
          <li><strong>Name</strong> {{ currentConversation.metadata.context.customer_name }}</li>
          <li><strong>IP Address</strong> {{ currentConversation.metadata.ip_address }}</li>
          <li><strong>Timezone</strong> {{ currentConversation.metadata.timezone }}</li>
          <li>
            <strong>Browser</strong> {{ convoUserAgent("browser", "name") }} {{ convoUserAgent("browser", "version") }}
          </li>
          <li><strong>Device</strong> {{ convoUserAgent("device", "name") }}</li>
          <li>
            <strong>Platform</strong> {{ convoUserAgent("platform", "name") }}
            {{ convoUserAgent("platform", "version") }}
          </li>
        </ul>

        <ul>
          <li class="title">Campain details</li>
          <li><strong>Id</strong> {{ metadata("context", "campaign_id") }}</li>
          <li><strong>Name</strong> {{ metadata("context", "campain_name") }}</li>
          <li><strong>Source</strong> {{ metadata("context", "campain_source") }}</li>
          <li><strong>Medium</strong> {{ metadata("context", "campain_medium") }}</li>
          <li><strong>Content</strong> {{ metadata("context", "campain_content") }}</li>
          <li><strong>Term</strong> {{ metadata("context", "campain_term") }}</li>
        </ul>

        <ul v-if="collectedFormdata.length">
          <li class="title">Collected Formdata</li>
          <li v-for="fd in collectedFormdata" :key="fd.key">
            <strong>{{ fd.key }}</strong> {{ fd.value }}
          </li>
        </ul>

        <ul v-if="customContext.length">
          <li class="title">Custom Context</li>
          <li v-for="c in customContext" :key="c.key">
            <strong>{{ c.key }}</strong> {{ c.value }}
          </li>
        </ul>

        <action-button
          class="button -full-width -not-rounded"
          :class="{ '-danger': !currentConversation.blacklisted }"
          :text="currentConversation.blacklisted ? 'Remove from blacklist' : 'Blacklist conversation'"
          :state="blacklistButtonState"
          @pressed="blacklistToggle"
        >
        </action-button>
      </section>
    </div>
  </div>
</template>

<script>
import dig from "object-dig";
import _ from "underscore";
import { mapGetters, mapState } from "vuex";

import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import actionButton from "@/components/actionButton.vue";
import conversationFlag from "@/components/conversationFlag.vue";
import conversationLabel from "@/components/conversationLabel.vue";
import messageList from "@/components/messageList.vue";
import replyBox from "@/components/replyBox.vue";
import testModeToggle from "@/components/testModeToggle.vue";
import auth from "@/helpers/auth";

export default {
  components: { conversationLabel, conversationFlag, messageList, replyBox, actionButton, testModeToggle, VueDatePicker },

  beforeRouteEnter(to, from, next) {
    if (!auth.signedIn()) return next("/sign-in");
    next();
  },

  data() {
    return {
      fetchingStatus: "ready",
      loadingMessages: false,
      loadMoreState: "normal",
      filtersOpen: false,
      filterState: "normal",
      filters: {
        status: "all",
        from: new Date(new Date().setDate(new Date().getDate() - 13)),
        to: new Date(),
        name: "",
        context: [],
        inc_blacklisted: false,
        dealership_id: null
      },
      collectedFormdata: [],
      customContext: [],
      blacklistButtonState: "normal"
    };
  },

  computed: {
    ...mapState({
      conversations: (state) => state.conversations.all,
      currentConversation: (state) => state.conversations.current,
      dealerships: (state) => state.dealerships.all,
      hasMoreConversations: (state) => state.conversations.hasMore,
      testMode: (state) => state.conversations.testMode
    }),
    ...mapGetters(["messagesForConversation", "currentTeam"]),

    activeConversations() {
      return this.conversations.filter((c) => c.status !== "pending");
    }
  },

  watch: {
    currentTeam() {
      this.setContextFitlers();
    },

    $route(to, from) {
      const id = parseInt(to.params.id, 10);

      // ID is not what is currently loaded... change it
      if (id && id !== this.currentConversation.id) {
        this.changeConversation({ id: id });
      }
    },

    currentConversation(newValue) {
      if (newValue) {
        this.$router.push({ path: `/conversations/${newValue.id}` });
        this.fetchMessages(newValue.id);

        // Set collected formdata
        this.collectedFormdata = [];
        _.each(newValue.formdata, (value, key) => {
          if (key !== "customer_name") {
            this.collectedFormdata.push({ key: key, value: value || "-" });
          }
        });

        // Set custom context
        this.customContext = [];
        _.each(newValue.metadata.context, (value, key) => {
          if (!_.contains(["customer_name", "image", "setters", "title", "url"], key)) {
            // TODO: make this recursive for infinite stacking?
            if (_.isObject(value)) {
              _.each(value, (subValue, subKey) => {
                this.customContext.push({ key: key + "." + subKey, value: subValue || "-" });
              });
            } else {
              this.customContext.push({ key: key, value: value || "-" });
            }
          }
        });
      }
    }
  },

  created() {
    this.initialFetch();
    this.setContextFitlers();
  },

  destroyed() {
    this.$store.commit("clearConversations");
  },

  methods: {
    initialFetch() {
      const convoId = this.$route.params.id;

      this.doFilter(() => {
        if (convoId) this.changeConversation({ id: parseInt(convoId, 10) });
      });
    },

    fetchMessages(conversationId) {
      this.loadingMessages = true;

      this.$store
        .dispatch("fetchMessages", conversationId)
        .then(() => {
          this.loadingMessages = false;
        })
        .catch((err) => {
          console.error("Error fetching messages", err);
          this.loadingMessages = false;
        });
    },

    changeConversation(conversation) {
      this.$store.dispatch("changeCurrentConversation", conversation.id);
    },

    postMessage(message) {
      const params = { message, conversationId: this.currentConversation.id };
      this.$store.dispatch("postMessage", params);
    },

    takeControl(inTheLoop) {
      const params = { id: this.currentConversation.id, human_in_the_loop: inTheLoop };
      this.$store.dispatch("updateConversation", params);
    },
    onFilterFromChange(val) {
      this.filters.from = val;
    },

    onFilterToChange(val) {
      this.filters.to = val;
    },

    loadMore() {
      this.loadMoreState = "loading";

      this.$store.dispatch("fetchConversations").then(() => {
        this.loadMoreState = "normal";
      });
    },

    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
    },

    setFilterFrom(val) {
      this.filters.from = val;
    },

    setFilterTo(val) {
      this.filters.to = val;
    },

    doFilter(callback = null) {
      this.fetchingStatus = "loading";

      this.$store.commit("clearConversations");
      this.$store.commit("setConversationFilters", this.filters);

      this.$store
        .dispatch("fetchConversations")
        .then(() => {
          this.fetchingStatus = "ready";
          callback();
        })
        .catch(() => {
          this.fetchingStatus = "failed";
        });
    },

    setContextFitlers() {
      _.each(this.currentTeam.settings.context_filters, (filter) => {
        this.filters.context.push({ label: filter.label, key: filter.key, value: "" });
      });
    },

    convoUserAgent(a, b) {
      return dig(this.currentConversation, "metadata", "user_agent", a, b);
    },

    metadata(a, b) {
      return dig(this.currentConversation,  a, b);
    },

    blacklistToggle() {
      this.blacklistButtonState = "loading";

      this.$store.dispatch("updateConversation", {
        id: this.currentConversation.id,
        blacklisted: !this.currentConversation.blacklisted,

        callback: () => {
          this.blacklistButtonState = "done";
          setTimeout(() => {
            this.blacklistButtonState = "normal";
          }, 3000);
        }
      });
    },

    onTestModeChange() {
      this.$store.commit("clearConversations");

      this.fetchingStatus = "loading";
      this.$store
        .dispatch("fetchConversations")
        .then(() => {
          this.fetchingStatus = "ready";
        })
        .catch(() => {
          this.fetchingStatus = "failed";
        });
    }
  }
};
</script>

<style>
.conv-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.conv-page-content {
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 15px;
  padding: 15px;
  height: 100%;
}

.conv-side {
  display: flex;
  flex-direction: column;
  width: 280px;
  min-width: 280px;
  height: 100%;
  border-right: 1px solid #ddd;
  padding-right: 15px;
  overflow-y: auto;
}

.conv-current {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-right: 1px solid #ddd;
  overflow: hidden;
}

.conv-current > :not(.conv-take-control) {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 0;
}

.conv-details {
  width: 280px;
  min-width: 280px;
  overflow-y: auto;
  padding-left: 15px;
}

.conv-details ul {
  margin: 0 0 20px;
  padding: 0;
  list-style: none;
}

.conv-details li {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.conv-details li.title {
  font-weight: bold;
  margin-top: 15px;
  border: none;
  padding: 10px 0 5px;
  font-size: 14px;
}

.conv-details li.title.mt0 {
  margin-top: 0;
}

.conv-details li.blacklisted {
  background-color: #fff3cd;
  color: #856404;
  padding: 10px;
  border: 1px solid #ffc107;
  border-radius: 4px;
  margin-bottom: 10px;
}

.conv-details li strong {
  display: inline-block;
  min-width: 150px;
}

.conv-details a {
  color: #3370d1;
  text-decoration: none;
}

.conv-details a:hover {
  text-decoration: underline;
}

.conv-filters {
  display: none;
  flex-direction: column;
  gap: 10px;
  padding: 15px 0;
  border-bottom: 1px solid #ddd;
  margin-bottom: 15px;
}

.conv-panel.-show-filters .conv-filters {
  display: flex;
}

.conv-filters select {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.conv-filters input {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.conv-filters label {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.conv-filters label.flex-label {
  display: flex;
  align-items: center;
  padding: 5px 0;
  border: none;
}

.conv-filters input[type='checkbox'] {
  width: auto;
  margin-right: 8px;
}

.conv-list {
  flex: 1;
  overflow-y: auto;
  min-height: 100px;
}

.conv-label {
  padding: 10px;
  border: 1px solid #eee;
  margin-bottom: 5px;
  cursor: pointer;
  border-radius: 4px;
}

.conv-label.empty {
  cursor: default;
  text-align: center;
  padding: 20px 10px;
  color: #999;
}

.conv-messages {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
}

.conv-take-control {
  flex-shrink: 0;
  max-height: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
  text-align: center;
  border-top: 1px solid #ddd;
}

.conv-take-control .button {
  background-color: #3370d1;
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-block;
  margin-bottom: 0;
  flex-shrink: 0;
  flex: none;
}

.conv-take-control .button:hover {
  background-color: #2556a0;
}

.conv-take-control p {
  margin: 0;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.conv-take-control .reply-box {
  flex-shrink: 0;
  flex: 0 0 auto;
  max-height: 150px;
  min-height: auto;
  overflow-y: auto;
}

.conv-auto-reply {
  display: block;
  margin-top: 0;
  color: #3370d1;
  cursor: pointer;
  text-decoration: underline;
  font-size: 13px;
  flex-shrink: 0;
  padding: 5px;
}

.conv-auto-reply:hover {
  text-decoration: none;
}

.conv-loading {
  display: flex;
  flex: 1;
  padding: 20px;
  text-align: center;
  color: #999;
  align-items: center;
  justify-content: center;
}

.conv-input-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.conv-input-field input,
.conv-input-field select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.conv-side .button.-toggle-filters {
  flex-shrink: 0;
  margin-bottom: 10px;
  padding: 10px;
  background-color: #3370d1;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  text-decoration: none;
  display: block;
  width: 100%;
  text-align: center;
}

.conv-side .button.-toggle-filters:hover {
  background-color: #2556a0;
}

@media (max-width: 1200px) {
  .conv-side {
    width: 260px;
    min-width: 260px;
  }

  .conv-details {
    width: 260px;
    min-width: 260px;
  }
}

@media (max-width: 768px) {
  .conv-page-content {
    flex-direction: column;
    gap: 0;
    padding: 10px;
  }

  .conv-side {
    width: 100%;
    min-width: 100%;
    border-right: none;
    border-bottom: 1px solid #ddd;
    padding-right: 0;
    padding-bottom: 0;
    margin-bottom: 10px;
  }

  .conv-current {
    border-right: none;
    border-bottom: 1px solid #ddd;
    padding-right: 0;
    padding-bottom: 0;
    margin-bottom: 10px;
  }

  .conv-details {
    width: 100%;
    min-width: 100%;
    padding-left: 0;
    padding-top: 0;
  }
}
</style>
